import { createLogger } from 'shared-utils/utils/logger'
import credentials from '../localeCredentials/credentials'
import { getRequestCredentials } from '../localeCredentials/credentialsStore'

interface FetchPropsFactory {
  props?: any
}

export interface UrlConfig<T> {
  queryParams?: T
  size?: number
  page?: number
}

export const getStoreFrontAuthHeaders = () => {
  // Prefer per-request credentials from AsyncLocalStorage. Fall back to the
  // env-default singleton for code paths that run outside a request's async
  // context (e.g. build-time generateStaticParams / app-config generation).
  const requestCredentials = getRequestCredentials()

  return {
    'X-Api-Key': requestCredentials?.apiKey ?? credentials.apiKey,
    Secret: requestCredentials?.secret ?? credentials.secret
  }
}

export const fetchPropsFactory = (config?: FetchPropsFactory): RequestInit => {
  const headers = {
    'Content-Type': 'application/json',
    ...getStoreFrontAuthHeaders()
  }

  return {
    method: 'GET',
    headers,
    ...(config ? config.props : {})
  }
}

export const createEndpointError = (
  endpointName: string,
  error?: string | StoreFrontApiErrorData | null
) => {
  const errStr =
    !!error && typeof error === 'object'
      ? `${error.status ?? ''} ${error.error}. ${
          error.messages?.length ? `\n${error.messages.join('. ')}` : ''
        }`
      : error
  throw new Error(
    `An error on the ${endpointName} endpoint occurred: ${errStr ? `\n${errStr}` : ''}`
  )
}

export const createError = (error: string | StoreFrontApiErrorData | null) => {
  const errStr =
    !!error && typeof error === 'object'
      ? `${error.status ?? ''} ${error.error}. ${
          error.messages?.length ? `\n${error.messages.join('. ')}` : ''
        }`.trim()
      : error
  return new Error(errStr ?? '')
}

export const urlWithQueryParamsFactory = <T>(url: string, config?: UrlConfig<T>) => {
  let updatedUrl = ''
  let paramIndex = 0

  const size = config?.size || 48
  const page = config?.page || 1

  if (config?.queryParams) {
    for (const param in config.queryParams) {
      if (paramIndex === 0) {
        updatedUrl = `?${param}=${config.queryParams[param]}`
      } else {
        updatedUrl = `${updatedUrl}&${param}=${config.queryParams[param]}`
      }
      paramIndex++
    }
  }

  return `${url}${updatedUrl}${updatedUrl ? '&' : '?'}size=${size}&page=${page}`
}

export interface ApiCallResponse<T> {
  error: string | null
  data: T | null
  response?: Response
}

const logger = createLogger('api')

export const apiCall = async <T>(url: string, props: RequestInit): Promise<ApiCallResponse<T>> => {
  let errorMsg = null
  let responseData = null
  let response: Response | undefined

  try {
    response = await fetch(url, props)

    // When No content statusCode 204 appears, if we try to parse it like response.json() error parsing will come up
    if (
      response.status !== 204 &&
      // content type might be, application/problem+json
      response.headers?.get('content-type')?.match(/\bapplication\/(?:[\w.+-]+)?json\b/gm) &&
      response.headers?.get('content-length') !== '0'
    ) {
      try {
        // Clone the response before consuming it to prevent "Body has already been consumed" errors
        const responseClone = response.clone()
        responseData = await responseClone.json()
      } catch (e) {
        errorMsg = `PARSING RESPONSE ERROR: ${e as string}`
      }
    }
  } catch (e) {
    logger.error(['apiCall fetch error'], url, props, e)
    errorMsg = 'API ERROR, CHECK LOGS TO DEBUG'
  }

  return {
    error: errorMsg,
    data: responseData,
    response
  }
}

export type StoreFrontApiErrorData = { status?: string; error: string; messages?: string[] }
export type StoreFrontApiCall<T> = T | StoreFrontApiErrorData

export const storefrontApiCall = async <T>(url: string, props: RequestInit) => {
  const { error, data, response } = await apiCall<StoreFrontApiCall<T>>(url, props)
  if (error || !data) {
    return {
      data: null,
      error,
      response
    }
  }

  // @ts-expect-error -- FIX
  if ('error' in data) {
    return {
      data: null,
      error: data,
      response
    }
  }

  return {
    data,
    error: null,
    response
  }
}
